import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './SubscriptionSuccess.css';
import { useAuthStore } from '../../store/useAuthStore';
import { useTranslation } from 'react-i18next';
import {clearBrowserStorage} from "../../utils/funnelHelpers";

interface LocationState {
  token?: string;
  email?: string;
  planName?: string;
  amount?: string;
}

const SubscriptionSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const setToken = useAuthStore((state) => state.setToken);
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Get email from URL parameters as fallback
  const getEmailFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('email');
  };

  useEffect(() => {
    const state = location.state as LocationState;
    const tokenFromState = state?.token;
    const tokenFromStorage = localStorage.getItem('jwt_token');

    // Get email from state or URL parameters as fallback
    const email = state?.email || getEmailFromUrl() || '';

    if (tokenFromState || tokenFromStorage) {
      setLoading(true);
      try {
        const token = tokenFromState || tokenFromStorage;
        if (token) {
          // setToken(token);
          setTimeout(() => {
            // navigate('/dashboard');
            clearBrowserStorage();
            window.location.href = `${process.env.REACT_APP_PRODUCT_URL}/pin?email=${email}`
          }, 4000);
        }
      } catch (err) {
        setError('Failed to process subscription');
      } finally {
        setLoading(false);
      }
    } else {
      setError('Invalid subscription data');
    }
  }, [location.state, navigate, setToken]);

  if (loading) {
    return <div>{t('subscriptionSuccess.processing')}</div>;
  }

  if (error) {
    return <div className="subscription-error">{error}</div>;
  }

  return (
    <div className="subscription-success-bg">
      <div className="subscription-success-container">
        <div className="subscription-success-card">
          {/* Top Icon */}
          <div className="subscription-success-icon">
            <img src="/images/circle-check-big.png" alt="Success" className="success-check-icon" />
          </div>

          {/* Main Title */}
          <h1 className="subscription-success-title">{t('subscriptionSuccess.welcomeTitle')}</h1>

          {/* Subtitle with emoji */}
          <p className="subscription-success-subtitle">
            {t('subscriptionSuccess.subtitle')}
          </p>

          {/* Plan Section */}
          <div className="subscription-plan-section">
            <span className="plan-label">{t('subscriptionSuccess.planLabel')}</span>
            <span className="plan-name">{(location.state as LocationState)?.planName || new URLSearchParams(window.location.search).get('planName') || t('subscriptionSuccess.defaultPlanName')}</span>
          </div>

          {/* Features Section */}
          <div className="subscription-features-section">
            <h2 className="features-title">{t('subscriptionSuccess.featuresTitle')}</h2>
            <div className="features-list">
              <div className="feature-item">
                <span className="feature-check">✅</span>
                <span>{t('subscriptionSuccess.features.courses')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-check">✅</span>
                <span>{t('subscriptionSuccess.features.personalized')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-check">✅</span>
                <span>{t('subscriptionSuccess.features.assistant')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-check">✅</span>
                <span>{t('subscriptionSuccess.features.certificates')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-check">✅</span>
                <span>{t('subscriptionSuccess.features.community')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-check">✅</span>
                <span>{t('subscriptionSuccess.features.updates')}</span>
              </div>
            </div>
          </div>

          {/* Support Section */}
          <div className="subscription-support-section">
            <p className="support-text">{t('subscriptionSuccess.supportText')}</p>
            <a href={`mailto:${t('subscriptionSuccess.supportEmail')}`} className="support-email">
              {t('subscriptionSuccess.supportEmail')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
